import { FeatureProfile } from './feature-profile.model';
import * as moment from 'moment';
import { Moment } from 'moment';
import { TmsType } from './tms-type.model';
import { BlujayCredentials } from './blujay-credentials.model';
import { Region } from './region.model';
import { LoadRangeDisplayCma } from './load-range-display-cma.model';
import { DriverAssignmentMode } from './driver-assignment-mode.model';
import { CMBLLangCode } from '../../core/languages';
import { ApiFamily } from './third-party.model';

export class Carrier {

  public id: string;
  public name: string;
  public code: string;
  public scacCode: string;
  public vendorNumbers: string[] = [];
  public vendorNumber: string;
  public emails: string[] = [];
  public tmsType: string;
  public blujayUrl: string;
  public blujayUrlLabel: string;
  public blujayCredentials: BlujayCredentials = new BlujayCredentials();
  public blueyonderCarrierCode: string;
  public blocked                              = false;
  public managedBy: string;
  public featureProfile: FeatureProfile;
  public lastTmsSynchronization: Moment;
  public region: Region;
  public tmsPickStartDateRequired             = false;
  public tmsLoadPickStartDate: Moment;
  public preferredTimezone: string;
  // Could be a FullyTypeSafeLanguagLocale but I preferred to keep string here in order to be sure that
  // we may receive a locale that is unknown by CMAWEB (this will force to use Language.isKnownLocaleLang()
  // to transform it into a FullyTypeSafeLanguagLocale)
  public preferredLocale: CMBLLangCode;
  public cmblApiToken: string;
  public bluechatCarrierCode: string;
  public apiFamily: ApiFamily = ApiFamily.REGULAR;

  public loadsFetchRange: string = LoadRangeDisplayCma.ALL;
  public isUsingBlujayTms: boolean;
  public isUsingBlueyonderTms: boolean;
  public isUsingLocalLoads: boolean;
  public selected = false;
  public eligibleToBluechatActivation: boolean;

  static fromDto(c): Carrier {
    if (!c) {
      return null;
    }

    const carrier = new Carrier(new FeatureProfile());

    carrier.id                       = c.id;
    carrier.name                     = c.name;
    carrier.code                     = c.code;
    carrier.scacCode                 = c.scacCode;
    carrier.emails                   = c.emails;
    carrier.vendorNumbers            = c.vendorNumbers;
    carrier.vendorNumber             = c.vendorNumber;
    carrier.tmsType                  = c.tmsType;
    carrier.blujayUrl                = c.blujayUrl;
    carrier.blujayUrlLabel           = c.blujayUrlLabel;
    carrier.blujayCredentials        = BlujayCredentials.fromDto(c.blujayCredentials);
    carrier.blueyonderCarrierCode    = c.blueyonderCarrierCode;
    carrier.blocked                  = c.blocked;
    carrier.managedBy                = c.managedBy;
    carrier.featureProfile           = FeatureProfile.fromDto(c.featureProfile);
    carrier.lastTmsSynchronization   = c.lastTmsSynchronization ? moment(c.lastTmsSynchronization) : null;
    carrier.region                   = Region.fromDto(c.region);
    carrier.tmsPickStartDateRequired = c.tmsPickStartDateRequired;
    carrier.tmsLoadPickStartDate     = c.tmsLoadPickStartDate ? moment(c.tmsLoadPickStartDate) : null;
    carrier.loadsFetchRange          = c.loadsFetchRange;
    carrier.isUsingBlujayTms         = carrier.tmsType === TmsType.BLUJAY;
    carrier.isUsingLocalLoads        = carrier.tmsType === TmsType.LOCAL || (carrier.featureProfile && carrier.featureProfile.tmsLoadsSynchronization);
    carrier.isUsingBlueyonderTms     = carrier.tmsType === TmsType.BLUEYONDER;
    carrier.preferredTimezone        = c.preferredTimezone;
    carrier.preferredLocale          = c.preferredLocale;
    carrier.cmblApiToken             = c.cmblApiToken;
    carrier.bluechatCarrierCode      = c.bluechatCarrierCode;
    carrier.apiFamily                = c.apiFamily ? c.apiFamily : ApiFamily.REGULAR;

    carrier.computeEligibleToBluechatActivation();

    return carrier;
  }

  static toDto(c: Carrier): any {
    if (!c) {
      return null;
    }

    return {
      ...c,
      featureProfile: FeatureProfile.toDto(c.featureProfile),
      region: Region.toDto(c.region)
    }
  }

  constructor(featureProfile: FeatureProfile) {
    this.featureProfile = featureProfile;
  }

  canAssignDriverInCmaweb() {
    if (!this.featureProfile || !this.region) {
      return true;
    }

    let mode = this.featureProfile.driverAssignmentModeFeature;

    if (this.featureProfile.driverAssignmentModeFeature === DriverAssignmentMode.INHERITED && this.region) {
      mode = this.region.features.driverAssignmentModeValue;
    }

    return mode === DriverAssignmentMode.CMAWEB || mode === DriverAssignmentMode.CMAWEB_AND_CMA;
  }

  bluechatEnabled(): boolean {
    return this.bluechatCarrierCode != null && this.isUsingBlueyonderTms;
  }

  computeEligibleToBluechatActivation() {
    this.eligibleToBluechatActivation = this.isUsingBlueyonderTms && this.bluechatCarrierCode === null;
  }
}
